/**
 * ClientDetail — Fiche client 360° pleine page (Sprint P1+P2, iter83).
 *
 * Rendu :
 *   - Header : nom + statut + boutons actions rapides (Nouvelle facture / Devis / Abonnement / Relance)
 *   - Alertes intelligentes (bandeaux colorés)
 *   - Bloc KPI 6 tuiles (CA 12m, CA total, Reste dû, En retard, DSO, Panier moyen)
 *   - 2 colonnes : Graph CA 12m (Recharts) + Top 5 produits achetés
 *   - Timeline activité chronologique (factures émises + paiements reçus)
 *   - Historique factures détaillé
 *   - Sidebar : coordonnées + notes + toggle récurrent
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, FileText, Plus, Repeat, Mail, Pencil, Star,
  TrendingUp, TrendingDown, AlertTriangle, Clock, CheckCircle2,
  Building2, MapPin, Phone, Save, Users, Package, Calendar,
  Sparkles, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ClientEditorSheet from "@/components/ClientEditorSheet";
import InvoiceDetailSheet from "@/components/InvoiceDetailSheet";

const eur = (n, prec = 0) => `${(Number(n) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: prec, maximumFractionDigits: prec,
})} €`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
                   "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const STATUS_META = {
  new:      { label: "🆕 Nouveau",   cls: "bg-sky-50 text-sky-700 border-sky-200" },
  vip:      { label: "🌟 VIP",       cls: "bg-purple-50 text-purple-700 border-purple-200" },
  active:   { label: "✅ Actif",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dormant:  { label: "💤 Dormant",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  at_risk:  { label: "⚠️ À risque",  cls: "bg-red-50 text-red-700 border-red-200" },
  prospect: { label: "🔍 Prospect",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
const ALERT_META = {
  danger:  { cls: "bg-red-50 border-red-200 text-red-800",       Icon: AlertTriangle },
  warning: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: Clock },
  success: { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: CheckCircle2 },
};
const INV_STATUS = {
  draft:     { label: "Brouillon", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  issued:    { label: "Émise",      cls: "bg-blue-50 text-blue-700 border-blue-200" },
  paid:      { label: "Payée",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partially_paid: { label: "Partielle", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  cancelled: { label: "Annulée",    cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

// =============================================================================
// KPI Tile
// =============================================================================
function KpiTile({ label, value, sub, icon: Icon, accent = "slate", testid }) {
  const accents = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    red: "bg-red-50 text-red-700 border-red-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <Card className="p-4 bg-white border-slate-200 rounded-xl" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</div>
          <div className="mt-1.5 text-xl font-semibold font-mono tabular-nums text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
            {value}
          </div>
          {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center border shrink-0", accents[accent])}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// Main
// =============================================================================
export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [invoiceSheet, setInvoiceSheet] = useState(null);
  const [notes, setNotes] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${id}`);
      setData(r.data);
      setNotes(r.data.client?.notes || "");
      setIsRecurring(!!r.data.client?.is_recurring);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app/clients");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveMeta = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${id}`, { notes: notes || null, is_recurring: isRecurring });
      toast.success("Modifications enregistrées");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // Quick actions — pré-remplir facture / devis / abonnement avec ce client
  const quickAction = (kind) => {
    if (!data) return;
    const c = data.client;
    const prefill = encodeURIComponent(JSON.stringify({
      client_id: c.id,
      client: {
        name: c.name, email: c.email, address: c.address,
        postal_code: c.postal_code, city: c.city, country: c.country,
        siret: c.siret, siren: c.siren, vat_number: c.vat_number,
      },
    }));
    if (kind === "invoice")     navigate(`/app/invoices/new?prefill=${prefill}`);
    else if (kind === "quote")   navigate(`/app/quotes?new=1&prefill=${prefill}`);
    else if (kind === "recurring") navigate(`/app/invoices/recurring?new=1&prefill=${prefill}`);
    else if (kind === "reminder") {
      // Send reminder email — hits invoices/{id}/remind for the oldest overdue
      const overdue = (data.invoices || []).find(
        (i) => i.status === "issued" && i.due_date && new Date(i.due_date) < new Date());
      if (!overdue) { toast.info("Aucune facture en retard à relancer"); return; }
      api.post(`/invoices/${overdue.id}/remind`)
        .then(() => toast.success(`Relance envoyée pour ${overdue.number}`))
        .catch((e) => toast.error(apiError(e)));
    }
  };

  const chartData = useMemo(() =>
    (data?.revenue_by_month || []).map((b) => ({
      month: fmtMonth(b.month), CA: b.ca_ttc, count: b.count,
    })), [data]);

  if (loading || !data) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement fiche client…
      </div>
    );
  }

  const c = data.client;
  const sMeta = STATUS_META[c.status] || STATUS_META.prospect;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* ─── Header ────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Link to="/app/clients" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-2"
                 data-testid="back-btn">
            <ArrowLeft className="w-3 h-3" /> Retour aux clients
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              {c.is_recurring && <Star className="inline w-6 h-6 text-amber-500 fill-amber-500 mr-1 -mt-1" />}
              {c.name}
            </h1>
            <Badge variant="outline" className={cn("text-xs", sMeta.cls)}>
              {sMeta.label}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {c.invoice_count} facture{c.invoice_count > 1 ? "s" : ""} · CA total {eur(c.revenue_total)}
            {c.city && ` · ${c.postal_code || ""} ${c.city}`}
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => quickAction("reminder")}
                     data-testid="qa-reminder-btn">
              <Mail className="w-4 h-4 mr-1.5" /> Relancer
            </Button>
            <Button variant="outline" onClick={() => quickAction("recurring")}
                     data-testid="qa-recurring-btn">
              <Repeat className="w-4 h-4 mr-1.5" /> Abonnement
            </Button>
            <Button variant="outline" onClick={() => quickAction("quote")}
                     data-testid="qa-quote-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Devis
            </Button>
            <Button onClick={() => quickAction("invoice")}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="qa-invoice-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouvelle facture
            </Button>
            <Button variant="ghost" onClick={() => setEditorOpen(true)}
                     data-testid="edit-client-btn">
              <Pencil className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* ─── Alertes intelligentes ─────────────────────────────── */}
      {(data.alerts || []).length > 0 && (
        <div className="space-y-2" data-testid="alerts-block">
          {data.alerts.map((a, i) => {
            const { cls, Icon } = ALERT_META[a.level] || ALERT_META.warning;
            return (
              <div key={i}
                    className={cn("px-4 py-2.5 rounded-lg border flex items-center gap-2.5 text-sm", cls)}
                    data-testid={`alert-${a.level}-${i}`}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="flex-1">{a.message}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── KPI 6 tuiles ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile label="CA 12 mois" value={eur(c.revenue_12m)} icon={TrendingUp} accent="blue"
                  testid="kpi-ca-12m" />
        <KpiTile label="CA cumulé" value={eur(c.revenue_total)} icon={Building2} accent="slate"
                  testid="kpi-ca-total" />
        <KpiTile label="Panier moyen" value={eur(c.avg_ticket)} icon={Package} accent="purple"
                  testid="kpi-avg" />
        <KpiTile label="Reste dû" value={eur(c.outstanding_ttc)} icon={Calendar} accent="amber"
                  testid="kpi-outstanding" />
        <KpiTile label="En retard" value={eur(c.overdue_ttc)} icon={AlertTriangle}
                  accent={c.overdue_ttc > 0 ? "red" : "slate"} testid="kpi-overdue" />
        <KpiTile label="DSO"
                  value={c.dso_days == null ? "—" : `${c.dso_days > 0 ? "+" : ""}${c.dso_days} j`}
                  sub={c.dso_days == null ? "aucune facture payée 12m"
                        : c.dso_days > 15 ? "élevé" : c.dso_days > 0 ? "correct" : "excellent"}
                  icon={Clock}
                  accent={c.dso_days == null ? "slate" : c.dso_days > 15 ? "red" : c.dso_days > 0 ? "amber" : "emerald"}
                  testid="kpi-dso" />
      </div>

      {/* ─── Row : Graph + Top produits + Coordonnées ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Graph CA 12m — 7 cols */}
        <Card className="lg:col-span-8 p-5 bg-white border-slate-200 rounded-xl" data-testid="chart-ca-12m">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900"
                   style={{ fontFamily: "Manrope, sans-serif" }}>
                CA sur 12 mois glissants
              </h3>
              <p className="text-xs text-slate-500">Basé sur les factures émises + payées (hors avoirs)</p>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase text-slate-500">Total 12m</div>
              <div className="text-lg font-semibold font-mono text-blue-700">{eur(c.revenue_12m)}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cli-ca-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
              <RTooltip
                contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
                formatter={(v, k) => k === "CA" ? [eur(v), "CA TTC"] : [v, "Nb factures"]}
              />
              <Area type="monotone" dataKey="CA" stroke="#2563eb" strokeWidth={2}
                    fill="url(#cli-ca-fill)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Top produits — 4 cols */}
        <Card className="lg:col-span-4 p-5 bg-white border-slate-200 rounded-xl" data-testid="top-products">
          <h3 className="text-sm font-semibold text-slate-900 mb-4"
               style={{ fontFamily: "Manrope, sans-serif" }}>
            Top 5 produits achetés
          </h3>
          {(data.top_products || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-8 text-center">
              Aucune ligne de facture engagée
            </div>
          ) : (
            <div className="space-y-3">
              {(data.top_products || []).map((p, i) => {
                const max = data.top_products[0].total_ht;
                const pct = max > 0 ? (p.total_ht / max) * 100 : 0;
                return (
                  <div key={i} data-testid={`top-product-${i}`}>
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="text-xs font-medium text-slate-800 truncate" title={p.name}>
                        {p.name}
                      </span>
                      <span className="text-xs font-mono font-semibold text-slate-900 shrink-0">
                        {eur(p.total_ht)}
                      </span>
                    </div>
                    <div className="h-1.5 mt-1 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                            style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {p.count} ligne{p.count > 1 ? "s" : ""} · Qté cumulée {p.quantity}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ─── Row: Timeline + Sidebar coordonnées ────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-8 p-5 bg-white border-slate-200 rounded-xl" data-testid="activity-timeline">
          <h3 className="text-sm font-semibold text-slate-900 mb-4"
               style={{ fontFamily: "Manrope, sans-serif" }}>
            Timeline d'activité
          </h3>
          {(data.activity || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic py-8 text-center">
              Aucune activité enregistrée
            </div>
          ) : (
            <div className="relative pl-6 border-l-2 border-slate-100 space-y-4 max-h-[420px] overflow-y-auto">
              {data.activity.map((ev, i) => {
                const isPay = ev.type === "payment";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="relative"
                    data-testid={`timeline-${i}`}
                  >
                    <div className={cn(
                      "absolute -left-[27px] top-1 w-3 h-3 rounded-full border-2 border-white",
                      isPay ? "bg-emerald-500" : "bg-blue-500")} />
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-sm">
                        {isPay ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                : <FileText className="w-3.5 h-3.5 text-blue-600" />}
                        <button
                          onClick={() => ev.target_id && setInvoiceSheet(ev.target_id)}
                          className="text-slate-800 hover:text-blue-700 hover:underline text-left">
                          {ev.label}
                        </button>
                      </div>
                      <span className="text-xs font-mono text-slate-500 whitespace-nowrap">{fmtDate(ev.at)}</span>
                    </div>
                    {ev.amount > 0 && (
                      <div className={cn(
                        "text-xs font-mono mt-0.5",
                        isPay ? "text-emerald-700 font-semibold" : "text-slate-500")}>
                        {isPay && "+ "}{eur(ev.amount)}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Sidebar : Coordonnées + Notes */}
        <div className="lg:col-span-4 space-y-4">
          <Card className="p-5 bg-white border-slate-200 rounded-xl" data-testid="contact-card">
            <h3 className="text-sm font-semibold text-slate-900 mb-3"
                 style={{ fontFamily: "Manrope, sans-serif" }}>Coordonnées</h3>
            <div className="space-y-2 text-sm text-slate-700">
              {c.email && (
                <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline truncate">{c.email}</a>
                </div>
              )}
              {c.phone && (
                <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</div>
              )}
              {(c.address || c.city) && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <span>{c.address}{c.postal_code && `, ${c.postal_code}`}{c.city && ` ${c.city}`}</span>
                </div>
              )}
              {c.siret && (
                <div className="flex items-center gap-2 font-mono text-xs"><Building2 className="h-3.5 w-3.5 text-slate-400" />SIRET {c.siret}</div>
              )}
              {c.vat_number && (
                <div className="flex items-center gap-2 font-mono text-xs"><Sparkles className="h-3.5 w-3.5 text-slate-400" />TVA {c.vat_number}</div>
              )}
            </div>
          </Card>

          <Card className="p-5 bg-white border-slate-200 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-medium text-slate-700">Client récurrent</label>
              <Switch checked={isRecurring} onCheckedChange={setIsRecurring}
                       data-testid="toggle-recurring" />
            </div>
            <div className="text-[10px] uppercase text-slate-500 mb-1.5">Notes internes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder="Contact commercial, préférences, remarques…"
                       rows={4}
                       data-testid="notes-textarea"
                       className="text-sm resize-none" />
            <Button size="sm" onClick={saveMeta} disabled={saving}
                     className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="save-meta-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1.5" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      </div>

      {/* ─── Historique factures ────────────────────────────────── */}
      <Card className="p-0 bg-white border-slate-200 rounded-xl overflow-hidden" data-testid="invoices-history">
        <div className="p-5 pb-3">
          <h3 className="text-sm font-semibold text-slate-900"
               style={{ fontFamily: "Manrope, sans-serif" }}>
            Historique factures ({data.invoices?.length || 0})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">N°</th>
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-4 py-2 font-semibold">Échéance</th>
                <th className="text-left px-4 py-2 font-semibold">Statut</th>
                <th className="text-right px-4 py-2 font-semibold">TTC</th>
                <th className="text-right px-4 py-2 font-semibold">Payé</th>
              </tr>
            </thead>
            <tbody>
              {(data.invoices || []).map((inv) => {
                const st = INV_STATUS[inv.status] || INV_STATUS.draft;
                return (
                  <tr key={inv.id}
                       onClick={() => setInvoiceSheet(inv.id)}
                       className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                       data-testid={`inv-row-${inv.id}`}>
                    <td className="px-4 py-2 font-mono text-blue-600 font-medium">{inv.number}</td>
                    <td className="px-4 py-2 text-slate-700 font-mono text-xs">{fmtDate(inv.issued_at)}</td>
                    <td className="px-4 py-2 text-slate-600 font-mono text-xs">{fmtDate(inv.due_date)}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={cn("text-[10px] uppercase", st.cls)}>
                        {st.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{eur(inv.total_ttc)}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700">
                      {inv.paid_amount_ttc > 0.01 ? eur(inv.paid_amount_ttc) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ─── Sheets ─────────────────────────────────────────────── */}
      <ClientEditorSheet
        clientId={id}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onSaved={() => load()}
      />
      <InvoiceDetailSheet
        invoiceId={invoiceSheet}
        open={!!invoiceSheet}
        onOpenChange={(v) => !v && setInvoiceSheet(null)}
      />
    </div>
  );
}
